import {
  useState, useEffect
} from 'react';
import {
 useLocation, useParams
} from 'react-router-dom';
import {
 Users, Database
} from 'lucide-react';
import {
 Card, CardContent, CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
 schemaService
} from 'shared/api/services/BEXO';
import {
 useRouting
} from './RoutingContext';
import {
 Column
} from './types/builderTables.types';

// Type for the table schema
interface Table {
    tableName: string;
}
export function DashboardView() {
    const location = useLocation();
    const {
 reloadKey
} = useRouting();
    const {
 oragID, appId
} = useParams<{ oragID: string; appId: string }>();
interface RecordItem {
  recordStatus: string;
  recordId: string;
  [key: string]: any; // ← allows additional dynamic fields like newField1, etc.
}

    // State variables with proper types
    const [error, setError] = useState<Error | null>(null);
    const [, setColumns] = useState<Column[]>([]);
    const [records, setRecords] = useState<RecordItem[]>([]);
    const [, setTables] = useState<Table[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [activeTableCount, setActiveTableCount] = useState<number>(0);

    // Fetch data on component mount and when location state changes
    useEffect(() => {
        if (location.state) {
            setColumns(location.state.data?.columns || []);
            setRecords(location.state.data?.records || []);

            const tablesData = location.state.tables || [];
            setTables(tablesData);
            setLoading(true);

            // Count unique table names
            const uniqueTableNames = new Set(tablesData.map((t: Table) => t.tableName));
            setActiveTableCount(uniqueTableNames.size);
        } else {
            setColumns([]);
            setRecords([]);
            setTables([]);
            setActiveTableCount(0);
        }
    }, [location.state]);

    // Fetch schemas from the API
    useEffect(() => {
        async function fetchSchemas() {
            try {
                     const res = await schemaService.retrieveSchemas(null, appId, oragID);
                            const schemas = res.data.schemas || [];

                            setTables(schemas);
                            localStorage.setItem('tables', JSON.stringify(schemas));

                            // Count unique tableNames
                            const uniqueTableNames = new Set(schemas.map((schema) => schema.tableName));
                            setActiveTableCount(uniqueTableNames.size);

            } catch (error) {
                console.error('Error fetching schemas:', error, appId, oragID);
                setError(error instanceof Error ? error : new Error('Unknown error')); // Type error correctly
            }
        }
        fetchSchemas();

    }, [reloadKey]);

    return (
        <div className="p-6 space-y-8 w-full mx-auto overflow-y-scroll">
            <div className="flex flex-col gap-2">
                <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
                <p className="text-muted-foreground">
                    Welcome to DataBuilder. Manage your database tables and relationships.
                </p>
            </div>

            {error && (
                <div className="text-red-600 font-semibold">
                    Error loading data: {error.message || 'Unknown error'}
                </div>
            )}

            {loading && <div>Loading records...</div>}

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2 w-full">
                <Card className="overflow-hidden border-none shadow-md transition-all hover:shadow-lg">
                    <CardHeader className="flex flex-row items-center justify-between pb-2 bg-gradient-to-r from-background to-muted/30">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            TOTAL USERS
                        </CardTitle>
                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                            <Users className="h-5 w-5 text-primary" />
                        </div>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="text-3xl font-bold tracking-tight">{records.length}</div>
                    </CardContent>
                </Card>

                <Card className="overflow-hidden border-none shadow-md transition-all hover:shadow-lg">
                    <CardHeader className="flex flex-row items-center justify-between pb-2 bg-gradient-to-r from-background to-muted/30">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                            ACTIVE TABLES
                        </CardTitle>
                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                            <Database className="h-5 w-5 text-primary" />
                        </div>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="text-3xl font-bold tracking-tight">{activeTableCount}</div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
